import { usePrevious } from '@mantine/hooks'
import type { SetStateAction } from 'jotai'
import type { Dispatch, ReactNode } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExtraPackageList } from 'src/features/ExtraPackage'
import MembershipPlanCard from 'src/features/Membership/components/MembershipPlanCard/MembershipPlanCardNew'
import { Interval } from 'src/services/apollo/generated/graphql'
import {
  type MembershipPlan,
  type MembershipSubscribeActionArgs,
  useMembershipPlans,
} from 'src/services/helpers/membership'
import { cn } from 'src/utils/cx'
import { getIsMobile } from 'src/utils/hooks/useBreakpoint'
import { startEventGroup, track } from 'src/utils/tracker'
import PriceTag from '~/components/PriceTag'
import Text from '~/components/ui/Text'
import { FEATURED_TIER } from '~/features/Membership/constants'
import { useCurrency } from '~/utils/currency'
import { useLocation } from '~/utils/router/route'
import {
  getDiscountTypeForTracking,
  getMembershipDiscountStylesByTier,
  useMembershipMonthlyDiscountedPrices,
  useUserIsEligibleForDiscount,
} from '../GrowthUnit/MembershipDiscount/discount-utils'
import {
  CreditPackDiscountCountdownBannerForNewUser,
  openMembershipDiscountClaimDialog,
} from '../GrowthUnit/MembershipDiscount/MemberDiscountUnits'
import { UIDialog, UIDialogContent } from '../ui/Modal'
import { MembershipSwitchModeBtn } from './MembershipItems'
import { MembershipPlansIntervalSwitch } from './MembershipPlansIntervalSwitch'

type DialogMode = 'plans' | 'packs'

/**
 * Prompt user before opening membership dialog
 */
export async function openProceedMembershipModal(args: {
  membershipModalProps?: MembershipMoreCreditsModalProps
  confirmationDialogProps: Parameters<typeof UIDialog.confirm>[0]
}) {
  const res = await UIDialog.confirm(args.confirmationDialogProps)
  if (res) {
    openMembershipMoreCreditsModal(args.membershipModalProps)
  }
}

export function openMembershipMoreCreditsModal(
  props?: MembershipMoreCreditsModalProps,
) {
  track(
    props?.defaultMode === 'packs' ? 'more_credit_click' : 'enter_member_click',
    {
      location: props?.trackingLocation,
      scene: props?.scene,
      displayType: 'dialog',
    },
  )
  startEventGroup('member_source', props?.trackingLocation, {
    persistent: true,
  })
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <MembershipMoreCreditsModalContent {...props} onClose={close} />
    ),
    type: getIsMobile() ? 'drawer' : 'dialog',
    className:
      'w-screen max-w-none mmd:w-[calc(100vw-64px)] mmd:max-w-screen-mlg',
  })
}

export type MembershipMoreCreditsModalRef = {
  open: () => void
}

export type MembershipMoreCreditsModalProps = Partial<
  {
    reason?: ReactNode
    trackingLocation: string
    defaultMode: DialogMode
    scene: string
    /**
     * Applies to extra pack purchases
     */
    afterPurchaseSuccess?: () => void
    onClose: () => void
    hideOneTimePurchase?: boolean
  } & Omit<MembershipSubscribeActionArgs, 'afterCancelSurvey'>
>

function MembershipMoreCreditsModalContent({
  reason,
  onClose,
  scene,
  afterPurchaseSuccess,
  extraParamsForSubscribeRedirection,
  defaultMode,
  hideOneTimePurchase,
}: MembershipMoreCreditsModalProps) {
  return (
    <MembershipMoreCreditsDialogContent
      reason={reason}
      onClose={() => {
        onClose?.()
      }}
      scene={scene}
      defaultMode={defaultMode}
      afterPurchaseSuccess={afterPurchaseSuccess}
      extraParamsForSubscribeRedirection={extraParamsForSubscribeRedirection}
      hideOneTimePurchase={hideOneTimePurchase}
    />
  )
}

const trackingArgs = {
  displayType: 'dialog',
}

function MembershipMoreCreditsDialogContent(
  props: Pick<
    MembershipMoreCreditsModalProps,
    | 'reason'
    | 'onClose'
    | 'scene'
    | 'defaultMode'
    | 'afterPurchaseSuccess'
    | 'extraParamsForSubscribeRedirection'
    | 'hideOneTimePurchase'
  >,
) {
  const [dialogMode, setDialogMode] = useState<DialogMode>(
    props.defaultMode ?? 'plans',
  )
  const [expanded, setExpanded] = useState(false)

  const switchBtn = (
    <MembershipSwitchModeBtn
      onClick={() => {
        track(
          dialogMode === 'plans'
            ? 'switch_membership_click'
            : 'switch_credit_packs_click',
          trackingArgs,
        )
        setDialogMode(prev => (prev === 'plans' ? 'packs' : 'plans'))
      }}
      mode={dialogMode}
      className="mr-4 flex items-center gap-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-500 py-1 px-1 relative"
    />
  )

  const location = useLocation()
  const prevLocation = usePrevious(location)

  // Close the modal when navigated away.
  // This is a workaround. Should find a more elegant way.
  useEffect(() => {
    if (prevLocation?.pathname && prevLocation.pathname !== location.pathname) {
      props.onClose?.()
    }
  }, [prevLocation?.pathname, location.pathname, props])

  return (
    <>
      <UIDialogContent className="">
        {props.reason && (
          <p className="px-4 py-2 mb-4 md:mr-4 bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400 rounded">
            {props.reason}
          </p>
        )}
        <div className="flex flex-col gap-4">
          {dialogMode === 'plans' && (
            <PlansPanel
              onClose={props.onClose}
              scene={props.scene}
              switchBtn={switchBtn}
              extraParamsForSubscribeRedirection={
                props.extraParamsForSubscribeRedirection
              }
              expanded={expanded}
              setExpanded={setExpanded}
            />
          )}
          {dialogMode === 'packs' && (
            <ExtraPackagesPanel
              switchBtn={switchBtn}
              afterPurchaseSuccess={() => {
                props.onClose?.()
                props.afterPurchaseSuccess?.()
              }}
              setMode={setDialogMode}
              hideOneTimePurchase={props.hideOneTimePurchase}
            />
          )}
        </div>
      </UIDialogContent>
    </>
  )
}

function ExtraPackagesPanel(props: {
  afterPurchaseSuccess?: () => void
  switchBtn?: ReactNode
  setMode: Dispatch<SetStateAction<'plans' | 'packs'>>
  hideOneTimePurchase?: boolean
}) {
  const { t } = useTranslation(['membership'])

  return (
    <>
      <div className="flex flex-wrap justify-between">
        <h1 className="text-3xl font-bold">
          {t('membership:more-credits.dialog.title')}
        </h1>
        {props.switchBtn}
      </div>
      <CreditPackDiscountCountdownBannerForNewUser className="mb-2 sm:mb-4" />
      <div className="flex flex-col">
        <ExtraPackageList
          afterSuccess={props.afterPurchaseSuccess}
          trackingArgs={trackingArgs}
          packListStyleProps={{
            className:
              'mx-auto w-fit grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 justify-items-center',
          }}
          setMode={props.setMode}
          hideOneTimePurchase={props.hideOneTimePurchase}
        />
      </div>
    </>
  )
}

function PlansPanel(
  props: {
    onClose?: () => void
    scene?: string
    switchBtn?: ReactNode
    expanded?: boolean
    setExpanded?: Dispatch<SetStateAction<boolean>>
  } & Pick<
    MembershipMoreCreditsModalProps,
    'extraParamsForSubscribeRedirection'
  >,
) {
  const { t } = useTranslation(['membership'])
  const [billingInterval, setBillingInterval] = useState<Interval>(
    Interval.Year,
  )

  const {
    data: { recallAvailable, commonAvailable },
  } = useUserIsEligibleForDiscount()

  const discountedPrices = useMembershipMonthlyDiscountedPrices(
    commonAvailable ? 'common' : undefined,
  )

  const plans = useMembershipPlans({
    discountedMonthlyPrices: discountedPrices,
  })

  return (
    <>
      <div className="">
        <div className="flex flex-wrap justify-between">
          <h1 className={cn('font-pixai-rounded', 'text-4xl')}>
            {t('membership:membership.label_long')}
          </h1>
          {props.switchBtn}
        </div>
        <div className="flex max-mmd:flex-col mmd:items-start gap-4">
          <div className="flex-1">
            <MembershipSubheading
              plans={plans}
              className="text-sm font-light mt-1"
            />
          </div>
        </div>
      </div>

      <section className="flex flex-col gap-8">
        <MembershipPlansIntervalSwitch
          billingInterval={billingInterval}
          setBillingInterval={setBillingInterval}
          plans={plans}
          className="mr-auto [&>span]:w-auto"
          trackingArgs={trackingArgs}
          hideSaleLabel={commonAvailable}
        />

        <div className="flex max-mmd:flex-col gap-4 justify-center">
          {plans.map(plan => {
            return (
              <MembershipPlanCard
                key={plan.id}
                {...plan}
                interval={billingInterval}
                featured={plan.tier === FEATURED_TIER}
                className="flex-1"
                style={
                  commonAvailable
                    ? getMembershipDiscountStylesByTier(plan.tier)
                    : undefined
                }
                trackingArgs={{
                  ...trackingArgs,
                  discountType: getDiscountTypeForTracking(
                    commonAvailable ? 'common' : undefined,
                  ),
                }}
                extraParamsForSubscribeRedirection={
                  props.extraParamsForSubscribeRedirection
                }
                afterCancelSurvey={
                  recallAvailable
                    ? () => {
                        track('membership_discount_popup', {
                          type: 'plans-dialog',
                        })
                        return openMembershipDiscountClaimDialog({
                          onClose: props.onClose,
                        })
                      }
                    : undefined
                }
                shouldDiscount={discountedPrices != null}
                discountComponent={
                  commonAvailable && billingInterval === Interval.Month
                    ? (args: { discount: number }) => (
                        <span className="text-xs">
                          {t(
                            'growth:member-discount-event.common-discount.sale-label',
                            { discount: args.discount },
                          )}
                        </span>
                      )
                    : undefined
                }
                setExpanded={props.setExpanded}
                expanded={props.expanded}
              />
            )
          })}
        </div>
      </section>
    </>
  )
}

function MembershipSubheading({
  plans,
  ...props
}: StyleProps & { plans: MembershipPlan[] }) {
  const [currency] = useCurrency()
  const lowestPricePerDay = useMemo(
    () =>
      Math.min(
        ...plans.map(plan => plan.prices[Interval.Year][currency].perDay),
      ),
    [currency, plans],
  )

  return Number.isFinite(lowestPricePerDay) ? (
    <p {...props}>
      <Text
        i18nKey="membership:plans-page.heading-content"
        components={{
          strong: <strong className="font-medium" />,
          pricePerDay: (
            <PriceTag currency={currency} value={lowestPricePerDay} />
          ),
        }}
      />
    </p>
  ) : (
    <>{'\u200B'}</>
  )
}
